import { formatDate } from '@simpleacs/shared';
/**
 * The Custom domain card's host rows, derived from `GET /api/branding/domain`
 * — pure, so every label and the poll rule are pinned in node.
 *
 * The operator asked for the card to "show which cnames are live" and to
 * "have the suggestion to autocreate the smartolt endpoint" (2026-09-14). The
 * API answers with one entry per host — the domain, and its SmartOLT-
 * compatible twin when the org has switched it on — each carrying what DNS
 * said, whether the block is enabled, whether it is on TLS and until when,
 * and the one sentence to act on. This file turns that into the words a row
 * shows and decides when the card keeps asking: every thirty seconds while
 * anything is not fully live (CNAME live, block enabled, TLS on), and not at
 * all once everything is green — the platform's watcher finishes a host
 * within five minutes of its DNS landing, and the card should show that
 * happening without a reload. It also shapes the apply's body: the form's
 * twin switch is sent on a first apply only (`applyBody`).
 */

/** What DNS answered for one host, as the API judged it. */
export interface CnameVerdict {
  ok: boolean;
  expected: string;
  found: string[];
  detail: string;
}

/** One host of the org's domain, as the API reports it. */
export interface DomainHost {
  role: 'app' | 'smartolt';
  host: string;
  cname: CnameVerdict;
  vhost: 'enabled' | 'absent';
  tls: { notAfter: string } | null;
  live: boolean;
  next: string | null;
}

/** The whole answer of GET /api/branding/domain. */
export interface DomainStatusView {
  domain: string | null;
  appliedAt: number | null;
  target: string;
  smartoltHost: string | null;
  smartoltEnabled: boolean;
  hosts: DomainHost[];
}

export type RowTone = 'ok' | 'warn' | 'muted';

/** A host row as the card paints it: three labelled cells and the sentence under them. */
export interface HostRow {
  role: 'app' | 'smartolt';
  host: string;
  cname: { label: string; tone: RowTone };
  vhost: { label: string; tone: RowTone };
  tls: { label: string; tone: RowTone };
  live: boolean;
  next: string | null;
}

/** How often the card re-reads the status while something is not live. */
export const DOMAIN_POLL_MS = 30_000;

/** `13 Dec 2026` — the day is what an operator reads off a certificate. */
export function formatNotAfter(iso: string): string {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return iso;
  return formatDate(t);
}

/** The CNAME cell: live, not resolving, or where it points instead. */
export function cnameCell(cname: CnameVerdict): HostRow['cname'] {
  if (cname.ok) return { label: 'live ✓', tone: 'ok' };
  if (cname.found.length === 0) return { label: 'not resolving', tone: 'warn' };
  return { label: `points at ${cname.found[0]!}`, tone: 'warn' };
}

/** The TLS cell: until the certificate's date, HTTP only while the block has none, pending while there is no block. */
export function tlsCell(host: Pick<DomainHost, 'vhost' | 'tls'>): HostRow['tls'] {
  if (host.tls) return { label: `until ${formatNotAfter(host.tls.notAfter)}`, tone: 'ok' };
  if (host.vhost === 'enabled') return { label: 'http only', tone: 'warn' };
  return { label: 'pending', tone: 'muted' };
}

export function hostRow(host: DomainHost): HostRow {
  return {
    role: host.role,
    host: host.host,
    cname: cnameCell(host.cname),
    vhost:
      host.vhost === 'enabled' ? { label: 'enabled', tone: 'ok' } : { label: 'not yet created', tone: 'muted' },
    tls: tlsCell(host),
    live: host.live,
    next: host.next,
  };
}

/** The rows in the API's order: the domain first, the twin (when switched on) under it. */
export function hostRows(status: Pick<DomainStatusView, 'hosts'> | undefined): HostRow[] {
  return (status?.hosts ?? []).map(hostRow);
}

/**
 * Whether the card has anything left to watch. No domain is nothing to
 * watch; a domain is watched until every host it lists is live.
 */
export function everyHostLive(status: Pick<DomainStatusView, 'domain' | 'hosts'> | undefined): boolean {
  if (!status?.domain) return true;
  return status.hosts.every((h) => h.live);
}

/**
 * The query's `refetchInterval`: thirty seconds while any host is not fully
 * live, off (false) once everything is green or there is no domain. Given to
 * TanStack Query as a function of the latest data, so a host going green
 * stops the polling on the very read that saw it.
 */
export function domainPollInterval(status: Pick<DomainStatusView, 'domain' | 'hosts'> | undefined): number | false {
  return everyHostLive(status) ? false : DOMAIN_POLL_MS;
}

/**
 * Whether the pre-apply form's twin switch has a say: only while no domain is
 * applied. Once one is live, the live box's switch is the org's wish and the
 * form is the TLS retry or a change of domain — either inherits that wish.
 */
export function formOffersSmartolt(status: Pick<DomainStatusView, 'domain'> | undefined): boolean {
  return !status?.domain;
}

/**
 * The body of `POST /api/branding/domain/apply`: `smartolt` travels ONLY on a
 * first apply. The API takes a given boolean over the row's flag, so a form
 * that always sent its switch — off by default — turned a live twin's wish
 * off with every re-apply from the card (the "re-create the host" TLS retry)
 * while the twin kept serving (review, 2026-09-14). With a domain live the
 * field is omitted and the row's wish stands, as the API's docs say it does.
 */
export function applyBody(
  status: Pick<DomainStatusView, 'domain'> | undefined,
  domain: string,
  withSmartolt: boolean,
): { domain: string; smartolt?: boolean } {
  return formOffersSmartolt(status) ? { domain, smartolt: withSmartolt } : { domain };
}
